import Box from "@mui/material/Box";

export type ChartLegendItem = { label: string; color: string; kind?: "bar" | "line"; dashed?: boolean };

/** Cinza legível (≥ 4,5:1 no branco) para rótulos de série clara demais. */
const READABLE_TEXT = "#5f6b73";

const channel = (c: number) => {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};

/** Contraste WCAG da cor `#rrggbb` contra o branco. */
function contrastOnWhite(hex: string): number {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return 21;
  const n = parseInt(m[1], 16);
  const l = 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
  return 1.05 / (l + 0.05);
}

/** O texto do item leva a cor da série; série pastel (contraste < 2:1 no branco, ex.: "Pessimista" #9fd8ea, 1,6:1) vira cinza legível — o swatch mantém a cor. */
export function legendTextColor(color: string): string {
  return contrastOnWhite(color) < 2 ? READABLE_TEXT : color;
}

/**
 * Legenda em HTML, fora do SVG (V32): a `Legend` do recharts mede a própria caixa depois do 1º render e,
 * enquanto não mede, o plot ocupa a altura toda e a legenda cai em cima dos rótulos do eixo X.
 * Aqui o fluxo do layout reserva o espaço desde o início.
 */
export default function ChartLegend({ items, note }: { items: ChartLegendItem[]; note?: string }) {
  return (
    <Box component="ul" data-testid="chart-legend"
      sx={{ listStyle: "none", m: 0, mt: 1, p: 0, display: "flex", flexWrap: "wrap", justifyContent: "center", columnGap: 2, rowGap: 0.5, fontSize: 13 }}>
      {items.map((it) => (
        <Box component="li" key={it.label} sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, color: legendTextColor(it.color) }}>
          <svg width={16} height={10} aria-hidden="true">
            {it.kind === "bar"
              ? <rect x={2} y={0} width={12} height={10} fill={it.color} />
              : <line x1={0} y1={5} x2={16} y2={5} stroke={it.color} strokeWidth={2} strokeDasharray={it.dashed ? "4 3" : undefined} />}
          </svg>
          {it.label}
        </Box>
      ))}
      {note && <Box component="li" sx={{ color: "text.secondary" }}>{note}</Box>}
    </Box>
  );
}
